import React, { useState, useEffect } from 'react';
import {
  Typography,
  Form,
  Input,
  Button,
  Select,
  DatePicker,
  message,
  Card,
  Space,
  Upload,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { getCities } from '../services/citiesService';
import { getCategories } from '../services/categoriesService';
import { uploadImage } from '../services/uploadService';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

function SubmitSomething() {
  const [form] = Form.useForm();
  const [submitType, setSubmitType] = useState(null);
  const [cities, setCities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [citiesData, categoriesData] = await Promise.all([getCities(), getCategories()]);
        setCities(citiesData);
        setCategories(categoriesData);
      } catch (err) {
        message.error('Failed to load form data');
      }
    };
    fetchData();
  }, []);

  const handleSubmit = async values => {
    setLoading(true);
    try {
      // Upload image if provided
      let imageUrl = null;
      if (values.image_file?.[0]?.originFileObj) {
        imageUrl = await uploadImage(values.image_file[0].originFileObj);
      }

      const formData =
        submitType === 'event'
          ? {
              ...values,
              start_date: values.dates[0]?.toISOString(),
              end_date: values.dates[1]?.toISOString(),
              city_id: cities[0]?.id, // Assuming the first city is selected
              image_url: imageUrl,
            }
          : {
              ...values,
              city_id: cities[0]?.id, // Assuming the first city is selected
              image_url: imageUrl,
            };

      formData.dates = undefined;

      // Remove the file object before sending
      delete formData.image_file;

      const response = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/submit-form`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            type: submitType,
            formData,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Submission failed');
      }

      message.success('Thank you for your submission! It will be reviewed by our team.');
      form.resetFields();
      setSubmitType(null);
    } catch (err) {
      message.error('Failed to submit. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        padding: '48px 24px',
        maxWidth: 800,
        margin: '0 auto',
      }}
    >
      <Title level={2}>Submit Something</Title>
      <Text style={{ marginBottom: 24, display: 'block' }}>
        Help us improve our community by submitting a place or event. Our team will review your
        submission.
      </Text>

      {!submitType ? (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Card hoverable onClick={() => setSubmitType('place')} style={{ marginBottom: 16 }}>
            <Title level={4}>Submit a Place</Title>
            <Text>Add a new business, landmark, or point of interest to our directory.</Text>
          </Card>

          <Card hoverable onClick={() => setSubmitType('event')}>
            <Title level={4}>Submit an Event</Title>
            <Text>Share an upcoming event with the community.</Text>
          </Card>
        </Space>
      ) : (
        <Card>
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Form.Item
              name="name"
              label="Name"
              rules={[{ required: true, message: 'Please enter a name' }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="description"
              label="Description"
              rules={[{ required: true, message: 'Please enter a description' }]}
            >
              <TextArea rows={4} />
            </Form.Item>

            {submitType === 'place' && (
              <Form.Item
                name="category_id"
                label="Category"
                rules={[{ required: true, message: 'Please select a category' }]}
              >
                <Select>
                  {categories.map(category => (
                    <Select.Option key={category.id} value={category.id}>
                      {category.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            )}

            {submitType === 'place' ? (
              <>
                <Form.Item name="address" label="Address">
                  <Input />
                </Form.Item>

                <Form.Item name="website_url" label="Website URL">
                  <Input />
                </Form.Item>

                <Form.Item name="phone" label="Phone">
                  <Input />
                </Form.Item>
              </>
            ) : (
              <Form.Item
                name="dates"
                label="Event Dates"
                rules={[{ required: true, message: 'Please select event dates' }]}
              >
                <RangePicker showTime />
              </Form.Item>
            )}

            <Form.Item
              name="image_file"
              label="Image"
              valuePropName="fileList"
              getValueFromEvent={e => {
                if (Array.isArray(e)) {
                  return e;
                }
                return e?.fileList;
              }}
            >
              <Upload maxCount={1} accept="image/*" beforeUpload={() => false} listType="picture">
                <Button icon={<UploadOutlined />}>Upload Image</Button>
              </Upload>
            </Form.Item>

            <Form.Item>
              <Space>
                <Button onClick={() => setSubmitType(null)}>Back</Button>
                <Button type="primary" htmlType="submit" loading={loading}>
                  Submit
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>
      )}
    </div>
  );
}

export default SubmitSomething;
